import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInstallPrompt, useIsAppInstalled, getInstallInstructions } from '../../utils/pwaUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faTimes, faExternalLinkAlt, faMobileAlt } from '@fortawesome/free-solid-svg-icons';

interface InstallBannerProps {
  className?: string;
}

const InstallBanner: React.FC<InstallBannerProps> = ({ className = '' }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const isInstalled = useIsAppInstalled();
  const { 
    isInstallable, 
    triggerInstall, 
    userChoice, 
    platform, 
    browser 
  } = useInstallPrompt();

  const instructions = getInstallInstructions(platform, browser);
  const isIOS = platform === 'ios';
  
  useEffect(() => {
    const hasDismissed = localStorage.getItem('pwa-banner-dismissed') === 'true';
    
    if (!isInstalled && isInstallable && !hasDismissed && !dismissed) {
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
    
    return undefined;
  }, [isInstalled, isInstallable, dismissed]);
  
  useEffect(() => {
    if (userChoice === 'accepted') {
      setShowBanner(false);
    }
  }, [userChoice]);

  const handleDismiss = () => {
    setShowBanner(false);
    setDismissed(true);
    localStorage.setItem('pwa-banner-dismissed', 'true');
  };

  const handleInstall = async () => {
    if (isIOS) {
      return;
    }
    
    const success = await triggerInstall();
    if (success) {
      setShowBanner(false);
    }
  };
  
  if (!showBanner || isInstalled) {
    return null;
  }

  // Animation variants
  const bannerVariants = {
    hidden: { y: 100, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { 
        type: "spring", 
        stiffness: 300, 
        damping: 30,
        duration: 0.5 
      }
    },
    exit: { 
      y: 100, 
      opacity: 0,
      transition: {
        duration: 0.3,
        ease: "easeInOut"
      }
    }
  };

  const contentVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        type: "spring",
        stiffness: 500,
        damping: 30
      }
    }
  };

  const buttonVariants = {
    hover: { 
      scale: 1.05,
      boxShadow: "0 5px 15px rgba(0, 0, 0, 0.1)"
    },
    tap: { 
      scale: 0.98,
      boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)"
    }
  };

  const iconMotion = {
    hidden: { opacity: 0, scale: 0.5 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 500,
        damping: 25,
      }
    }
  };

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          className={`install-banner ${className}`}
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={bannerVariants}
          style={{
            position: 'fixed',
            bottom: '16px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            width: 'calc(100% - 32px)',
            maxWidth: '500px',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15), 0 5px 15px rgba(0, 0, 0, 0.08)',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
          }}
        >
          {/* Animated Background */}
          <motion.div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(125deg, rgba(69, 104, 220, 0.9), rgba(176, 106, 179, 0.9))',
              zIndex: -1,
            }}
            animate={{
              background: [
                'linear-gradient(125deg, rgba(69, 104, 220, 0.9), rgba(176, 106, 179, 0.9))',
                'linear-gradient(225deg, rgba(69, 104, 220, 0.9), rgba(176, 106, 179, 0.9))',
                'linear-gradient(325deg, rgba(69, 104, 220, 0.9), rgba(176, 106, 179, 0.9))',
                'linear-gradient(125deg, rgba(69, 104, 220, 0.9), rgba(176, 106, 179, 0.9))'
              ]
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "linear"
            }}
          />

          {/* Grid Pattern Overlay */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px)',
              backgroundSize: '20px 20px',
              opacity: 0.5,
              zIndex: -1
            }}
          />

          <div
            style={{
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px'
            }}
          >
            {/* Icon */}
            <motion.div
              variants={iconMotion}
              data-icon-container
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <FontAwesomeIcon 
                icon={faMobileAlt} 
                style={{ 
                  color: 'white', 
                  fontSize: '24px' 
                }} 
              />
            </motion.div>

            {/* Content */}
            <motion.div 
              className="install-banner-content" 
              variants={contentVariants}
              style={{ 
                flex: 1,
                color: 'white' 
              }}
            >
              <motion.h5 
                variants={itemVariants}
                style={{ 
                  marginBottom: '4px', 
                  fontSize: '18px',
                  fontWeight: 'bold',
                  letterSpacing: '0.3px'
                }}
              >
                Install Our App
              </motion.h5>
              
              <motion.p 
                variants={itemVariants}
                style={{ 
                  fontSize: '14px', 
                  margin: '0',
                  opacity: 0.9,
                  fontWeight: '400',
                  lineHeight: 1.4
                }}
              >
                {isIOS ? instructions : 'Get fast access to our services'}
              </motion.p>
            </motion.div>
            
            {/* Action Buttons */}
            <div 
              className="install-banner-actions" 
              style={{ 
                display: 'flex', 
                gap: '8px',
                flexShrink: 0
              }}
            >
              {isIOS ? (
                <motion.button
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                  style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '10px 18px',
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '14px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={handleDismiss}
                >
                  <FontAwesomeIcon icon={faExternalLinkAlt} />
                  Got it
                </motion.button>
              ) : (
                <motion.button
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                  style={{
                    background: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '10px 18px',
                    color: '#4568dc',
                    fontWeight: '600',
                    fontSize: '14px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={handleInstall}
                >
                  <FontAwesomeIcon icon={faDownload} />
                  Install
                </motion.button>
              )}
              
              <motion.button
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
                style={{
                  background: 'rgba(0, 0, 0, 0.1)',
                  border: 'none',
                  borderRadius: '10px',
                  width: '36px',
                  height: '36px',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
                onClick={handleDismiss}
                aria-label="Dismiss"
              >
                <FontAwesomeIcon icon={faTimes} />
              </motion.button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default InstallBanner; 